/**
 * Single source of truth for the 1-click action set surfaced by both
 * the floating node toolbar (NodeFloatingToolbar — strict subset:
 * Duplicate, Lock, Delete) and the right-click context menu's
 * `kind === 'node'` branch (CanvasContextMenu — full set including
 * Copy + 4-way z-order). Adding an entry here lights it up in both
 * surfaces; each surface filters down via an explicit ID list.
 *
 * Mirrors the shape of frameActions.ts so a future refactor can hoist
 * the common types into a shared interface (FrameAction / NodeAction /
 * EdgeAction / MultiAction). Today we keep the types parallel-but-
 * separate so each kind can evolve its handler set independently
 * without churn.
 *
 * Scope: 1-click actions only. Formatting (Fill/Border, Font, Align,
 * TextStyle) lives in primitives/formatting/* — its UX is popover-
 * based and doesn't fit the registry's "single click → run" shape.
 */

export interface NodeActionContext {
  nodeId: string;
  locked: boolean;
  onCopy: (id: string) => void;
  onDuplicate: (id: string) => void;
  onBringToFront: (id: string) => void;
  onBringForward: (id: string) => void;
  onSendBackward: (id: string) => void;
  onSendToBack: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
}

export interface NodeAction {
  id:
    | 'copy'
    | 'duplicate'
    | 'bring-to-front'
    | 'bring-forward'
    | 'send-backward'
    | 'send-to-back'
    | 'lock'
    | 'delete';
  label: string;
  icon: string;
  shortcut?: string;
  danger?: boolean;
  labelFor?: (ctx: NodeActionContext) => string;
  iconFor?: (ctx: NodeActionContext) => string;
  available: (ctx: NodeActionContext) => boolean;
  run: (ctx: NodeActionContext) => void;
}

export const NODE_ACTIONS: NodeAction[] = [
  {
    id: 'copy',
    label: 'Copy',
    icon: 'content_copy',
    available: (c) => !c.locked,
    run: (c) => c.onCopy(c.nodeId),
  },
  {
    id: 'duplicate',
    label: 'Duplicate',
    icon: 'file_copy',
    available: (c) => !c.locked,
    run: (c) => c.onDuplicate(c.nodeId),
  },
  {
    id: 'bring-to-front',
    label: 'Bring to front',
    icon: 'flip_to_front',
    available: (c) => !c.locked,
    run: (c) => c.onBringToFront(c.nodeId),
  },
  {
    id: 'bring-forward',
    label: 'Bring forward',
    icon: 'move_up',
    available: (c) => !c.locked,
    run: (c) => c.onBringForward(c.nodeId),
  },
  {
    id: 'send-backward',
    label: 'Send backward',
    icon: 'move_down',
    available: (c) => !c.locked,
    run: (c) => c.onSendBackward(c.nodeId),
  },
  {
    id: 'send-to-back',
    label: 'Send to back',
    icon: 'flip_to_back',
    available: (c) => !c.locked,
    run: (c) => c.onSendToBack(c.nodeId),
  },
  {
    id: 'lock',
    // Locked → menu shows "Unlock"; unlocked → "Lock". Surfaces choose
    // their own label/icon logic via labelFor/iconFor when they care
    // about state-aware text (the menu does; the floating toolbar uses
    // HoldToUnlockButton for locked nodes so this entry never renders
    // as Unlock there).
    label: 'Lock',
    icon: 'lock',
    labelFor: (c) => (c.locked ? 'Unlock' : 'Lock'),
    iconFor: (c) => (c.locked ? 'lock_open' : 'lock'),
    // Always available — locked nodes need Unlock; unlocked need Lock.
    available: () => true,
    run: (c) => c.onToggleLock(c.nodeId),
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: 'delete',
    danger: true,
    available: () => true,
    run: (c) => c.onDelete(c.nodeId),
  },
];
